import { createClient, SupabaseClient } from '@supabase/supabase-js';

// trim(): the Vercel value of the key ended in a newline (found 2026-10-11). Fetch headers drop it,
// but Realtime puts the key in the WebSocket URL as `%0A` and refuses the connection.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();

// During Next.js build env vars may be missing; runtime always has them.
// Cast to SupabaseClient so downstream .from()/.select() inference stays intact.
export const supabase: SupabaseClient = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : (null as unknown as SupabaseClient);

/**
 * `fetch` with the signed-in user's access token on it, for calling our own `/api/*` routes from
 * a page. Those routes read the token with `getAuthenticatedEmail()`; without the header they
 * answer 401.
 *
 * This is the shared home so a third copy never gets written. Two byte-identical local copies
 * exist today — `app/_protected/exit-tracker/page.tsx` and
 * `app/_protected/exit-tracker/_components/exit-case-panel.tsx` — plus the same inline
 * `getSession()` + Bearer pattern in several report pages. Rewiring them is separate work.
 */
export async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  // `new Headers(...)`, never a spread. RequestInit['headers'] is a Headers instance OR an array
  // of [name, value] pairs OR a plain object, and spreading either of the first two yields `{}` —
  // the caller's headers would vanish with no error. The Headers constructor takes all three.
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}

export default supabase;
